import { useEffect } from 'react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { usePageHeader } from '../../providers/PageHeaderProvider';
import { DmMessagePanel } from '../../components/monitoring/messages/DmMessagePanel';
import { MessagesTable } from '../../components/monitoring/messages/MessagesTable';
import { useMessagePanelSelection } from '../../components/monitoring/messages/useMessagePanelSelection';
import { useMessagesInbox } from '../../components/monitoring/messages/useMessagesInbox';
import { RefreshingHint } from '../../components/monitoring/shared/RefreshingHint';
import type { DmMessageInboxMeta } from '../../services/dmMessagesInbox';
import {
    CLOSED_WINDOW,
    patientsAndEventsLabel,
    resolvedPatientsLabel
} from '../../components/monitoring/shared/monitoringCounts';

const DEFAULT_SUBTITLE = 'Dental Monitoring patient messages waiting to be reviewed';
const CLOSED_SUBTITLE = `Dental Monitoring patient messages ${CLOSED_WINDOW}`;

const KEEP_ROW = (): Promise<void> => Promise.resolve();

function headerSubtitle(meta: DmMessageInboxMeta | null, closed: boolean): string {
    if (closed) return meta ? resolvedPatientsLabel(meta.total) : CLOSED_SUBTITLE;
    if (!meta) return DEFAULT_SUBTITLE;
    return patientsAndEventsLabel(meta.total, meta.totalMessages, 'message');
}

export function MonitoringMessages() {
    usePageTitle('Monitoring · Messages');
    const { setTitle, setSubtitle } = usePageHeader();
    const inbox = useMessagesInbox();
    const { selectedPatientId, openPatient, closePanel } = useMessagePanelSelection();
    // A closed row counts closed messages, not ones to review, so the panel reads the patient itself.
    const closed = inbox.table.status === 'closed';
    const subtitle = headerSubtitle(inbox.query.meta, closed);
    const refreshing = inbox.query.isShowingPrevious && !inbox.query.isLoading;

    useEffect(() => {
        setTitle('Messages');
        return () => {
            setTitle(null);
            setSubtitle(null);
        };
    }, [setTitle, setSubtitle]);

    // Built here from primitives: a node made during render would re-set the header every render.
    useEffect(() => {
        setSubtitle(
            <>
                {subtitle}
                {refreshing && <RefreshingHint />}
            </>
        );
    }, [subtitle, refreshing, setSubtitle]);

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            <MessagesTable inbox={inbox} onOpenPatient={openPatient} />
            {selectedPatientId && (
                <DmMessagePanel
                    key={selectedPatientId}
                    patientId={selectedPatientId}
                    listed={closed ? null : inbox.findListedPatient(selectedPatientId)}
                    listSearch={inbox.table.listSearch}
                    onReviewed={closed ? KEEP_ROW : inbox.markPatientLeaving}
                    onClose={closePanel}
                />
            )}
        </div>
    );
}
